/**
 * Reading an HTML form the way a browser would submit it - with no DOM, no network and no
 * platform API at all.
 *
 * Authority: the supervised capture of TMS's technical-officials write flow (18 September 2026)
 * and the read-only survey of the real day page (the 20 September men's day, competition 1916).
 * The capture found that ONE plain form post saves a whole matchday for a competition. The only
 * safe way to send such a post is to send back exactly what the TD's own browser would send for
 * the page he is looking at, changing nothing but the fields we mean to change. This module is
 * the "exactly what the browser would send" half of that sentence; `tms-technical-day.ts` is the
 * other half.
 *
 * WHY A HAND-WRITTEN READER
 *
 * The same code has to run in the API server, in a browser page and in a Chrome MV3 extension
 * service worker. The service worker has no HTML parser object to borrow, so a reader built on
 * one would need a second implementation there - and two readers of the one form that decides
 * what TMS overwrites is one too many. So this is a small tokenizer and a cut-down tree builder
 * written against the HTML standard's own parsing rules, doing only what form submission needs:
 *
 *  * tags and attributes in any order and any case, quoted, unquoted or bare; comments and
 *    bogus comments; raw-text elements (a script holding "<select ...>" in a string is text, not
 *    a select), including the script data escaped and double escaped states, by which a script
 *    that wraps itself in "<!-- ... -->" can carry a "</script>" that does not end it;
 *    character references in values;
 *  * which form owns each control: the parser's form pointer, the `form` attribute, a nested
 *    form start tag that the parser drops, a control left inside a form after its end tag;
 *  * what is disabled: the attribute, and a disabled fieldset - except inside its first legend;
 *  * each select's selected option, by the standard's selectedness rules (the last `selected`
 *    wins; with none, the first option that is not disabled);
 *  * table structure, well enough to say which row and column a control sits in, and to notice
 *    when a browser would move a control out of its table (foster parenting).
 *
 * STRICT, NEVER GUESSING
 *
 * Anything this reader cannot reproduce with certainty is reported as a PROBLEM rather than
 * approximated: a select the page never closes, a control a browser would move, a named
 * character reference it does not know inside a value that would be sent, an input type whose
 * value a browser rewrites before sending, two submit buttons that would send different things.
 * A caller that gets a problem must refuse to post. Nothing here ever fills a gap with a guess,
 * because a guess here is a field TMS overwrites.
 *
 * Everything below is ASCII on purpose, including the character-reference tables (code points
 * are written as numbers), so this file is byte-identical on every machine that builds it.
 */
/**
 * Pages larger than this are refused unread. TMS's day page is tens of kilobytes; the limit is
 * there so that a hostile or broken answer cannot make the reader spend minutes or gigabytes.
 */
export const MAX_HTML_PAGE_CHARS = 3 * 1024 * 1024;
/**
 * Elements nested deeper than this make the page refused. Real pages nest a few dozen deep; the
 * limit bounds every walk of the open-element stack, which is what kept deep nesting quadratic.
 */
export const MAX_HTML_DEPTH = 512;
/** At most this many elements' text is collected, each to at most this many characters. */
const MAX_COLLECTED_ELEMENTS = 100;
const MAX_COLLECTED_CHARS = 2000;
/** A table cell's text is kept to this many characters: enough for a Time cell, never a page. */
const MAX_CELL_CHARS = 500;
/**
 * Links, and labels, kept per page. A marking index for a whole tournament panel holds a few
 * hundred; past this the page says so (`linksTruncated`) rather than being silently cut.
 */
export const MAX_HTML_LINKS = 5000;
/** A link's or a label's text is kept to this many characters. */
const MAX_LINK_CHARS = 500;
/* -- Small character helpers -------------------------------------------------------------- */
function isAsciiWhitespace(c) {
    return c === " " || c === "\t" || c === "\n" || c === "\f" || c === "\r";
}
function isAsciiAlpha(c) {
    return c !== undefined && ((c >= "a" && c <= "z") || (c >= "A" && c <= "Z"));
}
function isDigit(c) {
    return c !== undefined && c >= "0" && c <= "9";
}
function isHexDigit(c) {
    return isDigit(c) || (c !== undefined && ((c >= "a" && c <= "f") || (c >= "A" && c <= "F")));
}
function isAsciiAlphanumeric(c) {
    return isAsciiAlpha(c) || isDigit(c);
}
/** HTML lowercases names in ASCII only; `toLowerCase` would also fold other scripts. */
export function asciiLower(value) {
    return value.replace(/[A-Z]/g, (c) => String.fromCharCode(c.charCodeAt(0) + 32));
}
/** Strip and collapse ASCII whitespace - the standard's rule for option text and similar. */
export function collapseWhitespace(value) {
    return value.replace(/[\t\n\f\r ]+/g, " ").replace(/^ /, "").replace(/ $/, "");
}
/* -- Character references ----------------------------------------------------------------- */
/**
 * The named references a browser decodes even WITHOUT a closing semicolon. The standard fixes
 * this set and it is complete here, which is what lets the no-semicolon case be decoded
 * exactly. A Map rather than an object, so that a name such as `&constructor;` can never find
 * something on a prototype.
 */
const LEGACY_REFERENCES = new Map(Object.entries({
    AElig: 0xc6, AMP: 0x26, Aacute: 0xc1, Acirc: 0xc2, Agrave: 0xc0, Aring: 0xc5, Atilde: 0xc3,
    Auml: 0xc4, COPY: 0xa9, Ccedil: 0xc7, ETH: 0xd0, Eacute: 0xc9, Ecirc: 0xca, Egrave: 0xc8,
    Euml: 0xcb, GT: 0x3e, Iacute: 0xcd, Icirc: 0xce, Igrave: 0xcc, Iuml: 0xcf, LT: 0x3c,
    Ntilde: 0xd1, Oacute: 0xd3, Ocirc: 0xd4, Ograve: 0xd2, Oslash: 0xd8, Otilde: 0xd5,
    Ouml: 0xd6, QUOT: 0x22, REG: 0xae, THORN: 0xde, Uacute: 0xda, Ucirc: 0xdb, Ugrave: 0xd9,
    Uuml: 0xdc, Yacute: 0xdd, aacute: 0xe1, acirc: 0xe2, acute: 0xb4, aelig: 0xe6,
    agrave: 0xe0, amp: 0x26, aring: 0xe5, atilde: 0xe3, auml: 0xe4, brvbar: 0xa6,
    ccedil: 0xe7, cedil: 0xb8, cent: 0xa2, copy: 0xa9, curren: 0xa4, deg: 0xb0, divide: 0xf7,
    eacute: 0xe9, ecirc: 0xea, egrave: 0xe8, eth: 0xf0, euml: 0xeb, frac12: 0xbd,
    frac14: 0xbc, frac34: 0xbe, gt: 0x3e, iacute: 0xed, icirc: 0xee, iexcl: 0xa1,
    igrave: 0xec, iquest: 0xbf, iuml: 0xef, laquo: 0xab, lt: 0x3c, macr: 0xaf, micro: 0xb5,
    middot: 0xb7, nbsp: 0xa0, not: 0xac, ntilde: 0xf1, oacute: 0xf3, ocirc: 0xf4,
    ograve: 0xf2, ordf: 0xaa, ordm: 0xba, oslash: 0xf8, otilde: 0xf5, ouml: 0xf6, para: 0xb6,
    plusmn: 0xb1, pound: 0xa3, quot: 0x22, raquo: 0xbb, reg: 0xae, sect: 0xa7, shy: 0xad,
    sup1: 0xb9, sup2: 0xb2, sup3: 0xb3, szlig: 0xdf, thorn: 0xfe, times: 0xd7, uacute: 0xfa,
    ucirc: 0xfb, ugrave: 0xf9, uml: 0xa8, uuml: 0xfc, yacute: 0xfd, yen: 0xa5, yuml: 0xff,
}));
/** The number of legacy names - fixed by the standard, and pinned by a test. */
export const LEGACY_REFERENCE_COUNT = LEGACY_REFERENCES.size;
const LONGEST_LEGACY_NAME = 6;
/** U+FFFD and U+0000, built from their numbers so that this file stays pure ASCII. */
const REPLACEMENT_CHARACTER = String.fromCharCode(0xfffd);
const NUL = String.fromCharCode(0);
/**
 * A few common names that are only references WITH their semicolon. The standard has over two
 * thousand; any other `&name;` makes the value uncertain (see {@link decodeCharacterReferences}).
 */
const SEMICOLON_REFERENCES = new Map(Object.entries({
    apos: 0x27, Tab: 0x09, NewLine: 0x0a, excl: 0x21, num: 0x23, dollar: 0x24, percnt: 0x25,
    lpar: 0x28, rpar: 0x29, ast: 0x2a, plus: 0x2b, comma: 0x2c, period: 0x2e, sol: 0x2f,
    colon: 0x3a, semi: 0x3b, equals: 0x3d, quest: 0x3f, commat: 0x40, lsqb: 0x5b,
    bsol: 0x5c, rsqb: 0x5d, Hat: 0x5e, lowbar: 0x5f, grave: 0x60, lcub: 0x7b, verbar: 0x7c,
    rcub: 0x7d, OElig: 0x152, oelig: 0x153, Scaron: 0x160, scaron: 0x161, Yuml: 0x178,
    fnof: 0x192, circ: 0x2c6, tilde: 0x2dc, ensp: 0x2002, emsp: 0x2003, thinsp: 0x2009,
    zwnj: 0x200c, zwj: 0x200d, lrm: 0x200e, rlm: 0x200f, ndash: 0x2013, mdash: 0x2014,
    lsquo: 0x2018, rsquo: 0x2019, sbquo: 0x201a, ldquo: 0x201c, rdquo: 0x201d, bdquo: 0x201e,
    dagger: 0x2020, Dagger: 0x2021, bull: 0x2022, hellip: 0x2026, permil: 0x2030,
    lsaquo: 0x2039, rsaquo: 0x203a, euro: 0x20ac, trade: 0x2122, larr: 0x2190, uarr: 0x2191,
    rarr: 0x2192, darr: 0x2193, minus: 0x2212,
}));
/** What `&#128;`..`&#159;` really mean: the standard maps them through Windows-1252. */
const C1_REPLACEMENTS = new Map([
    [0x80, 0x20ac], [0x82, 0x201a], [0x83, 0x0192], [0x84, 0x201e], [0x85, 0x2026],
    [0x86, 0x2020], [0x87, 0x2021], [0x88, 0x02c6], [0x89, 0x2030], [0x8a, 0x0160],
    [0x8b, 0x2039], [0x8c, 0x0152], [0x8e, 0x017d], [0x91, 0x2018], [0x92, 0x2019],
    [0x93, 0x201c], [0x94, 0x201d], [0x95, 0x2022], [0x96, 0x2013], [0x97, 0x2014],
    [0x98, 0x02dc], [0x99, 0x2122], [0x9a, 0x0161], [0x9b, 0x203a], [0x9c, 0x0153],
    [0x9e, 0x017e], [0x9f, 0x0178],
]);
function numericReference(digits, hex) {
    // A long run of digits is out of range whatever it says; parsing it would lose precision first.
    const significant = digits.replace(/^0+/, "");
    const value = significant.length > 8 ? Number.POSITIVE_INFINITY : Number.parseInt(digits, hex ? 16 : 10);
    if (value === 0 || !(value <= 0x10ffff) || (value >= 0xd800 && value <= 0xdfff))
        return REPLACEMENT_CHARACTER;
    return String.fromCodePoint(C1_REPLACEMENTS.get(value) ?? value);
}
/**
 * Decode character references the way the HTML tokenizer does, in an attribute value
 * (`inAttribute`) or in text.
 *
 * `certain` is false when the text holds `&name;` for a name this module does not carry: the
 * standard knows two thousand more, so whether a browser would decode it cannot be settled
 * here. The text is then kept as written, and a caller about to SEND that value must refuse.
 * Everything else - numeric references, the whole no-semicolon legacy set and its attribute
 * exception (`?a=1&copy=2` stays as written in an attribute) - is decoded exactly.
 */
export function decodeCharacterReferences(raw, inAttribute) {
    if (!raw.includes("&"))
        return { text: raw, certain: true };
    let out = "";
    let certain = true;
    let i = 0;
    while (i < raw.length) {
        const amp = raw.indexOf("&", i);
        if (amp < 0) {
            out += raw.slice(i);
            break;
        }
        out += raw.slice(i, amp);
        const start = amp + 1;
        if (raw[start] === "#") {
            let k = start + 1;
            const hex = raw[k] === "x" || raw[k] === "X";
            if (hex)
                k += 1;
            const digitsStart = k;
            while (k < raw.length && (hex ? isHexDigit(raw[k]) : isDigit(raw[k])))
                k += 1;
            if (k === digitsStart) {
                // "&#" or "&#x" with no digits is not a reference: it stays exactly as written.
                out += raw.slice(amp, k);
                i = k;
                continue;
            }
            const digits = raw.slice(digitsStart, k);
            if (raw[k] === ";")
                k += 1;
            out += numericReference(digits, hex);
            i = k;
            continue;
        }
        let k = start;
        while (k < raw.length && isAsciiAlphanumeric(raw[k]))
            k += 1;
        const run = raw.slice(start, k);
        if (run === "") {
            out += "&";
            i = start;
            continue;
        }
        if (raw[k] === ";") {
            const code = LEGACY_REFERENCES.get(run) ?? SEMICOLON_REFERENCES.get(run);
            if (code !== undefined) {
                out += String.fromCodePoint(code);
                i = k + 1;
                continue;
            }
            certain = false;
            out += raw.slice(amp, k + 1);
            i = k + 1;
            continue;
        }
        // No semicolon: only the legacy names can match, and the longest one does.
        let match = "";
        for (let length = Math.min(run.length, LONGEST_LEGACY_NAME); length > 0; length -= 1) {
            const candidate = run.slice(0, length);
            if (LEGACY_REFERENCES.has(candidate)) {
                match = candidate;
                break;
            }
        }
        if (match === "") {
            out += "&";
            i = start;
            continue;
        }
        if (inAttribute) {
            const next = raw[start + match.length];
            if (next === "=" || isAsciiAlphanumeric(next)) {
                // The standard's historical exception for attribute values.
                out += "&";
                i = start;
                continue;
            }
        }
        out += String.fromCodePoint(LEGACY_REFERENCES.get(match) ?? 0xfffd);
        i = start + match.length;
    }
    return { text: out, certain };
}
/* -- Element categories (from the parsing section of the HTML standard) -------------------- */
const VOID_ELEMENTS = new Set([
    "area", "base", "basefont", "bgsound", "br", "col", "embed", "frame", "hr", "img", "image",
    "input", "keygen", "link", "meta", "param", "source", "track", "wbr",
]);
/** Elements whose content is text up to their own end tag (with scripting enabled). */
const RAW_TEXT_ELEMENTS = new Set([
    "script", "style", "xmp", "iframe", "noembed", "noframes", "noscript", "title", "textarea",
]);
/** Start tags that first close an open `<p>` in button scope. */
const CLOSES_P = new Set([
    "address", "article", "aside", "blockquote", "center", "details", "dialog", "dir", "div", "dl",
    "fieldset", "figcaption", "figure", "footer", "header", "hgroup", "main", "menu", "nav", "ol",
    "p", "search", "section", "summary", "ul", "h1", "h2", "h3", "h4", "h5", "h6", "pre",
    "listing", "li", "dd", "dt", "table", "hr", "xmp",
]);
/** End tags that close their element when it is in scope, popping whatever is open inside it. */
const BLOCK_END_TAGS = new Set([
    "address", "article", "aside", "blockquote", "button", "center", "details", "dialog", "dir",
    "div", "dl", "fieldset", "figcaption", "figure", "footer", "header", "hgroup", "listing",
    "main", "menu", "nav", "ol", "pre", "search", "section", "summary", "ul", "applet", "marquee",
    "object",
]);
const HEADINGS = new Set(["h1", "h2", "h3", "h4", "h5", "h6"]);
/** The "special" category: an end tag for anything else cannot close through one of these. */
const SPECIAL_HTML = new Set([
    "address", "applet", "area", "article", "aside", "base", "basefont", "bgsound", "blockquote",
    "body", "br", "button", "caption", "center", "col", "colgroup", "dd", "details", "dir", "div",
    "dl", "dt", "embed", "fieldset", "figcaption", "figure", "footer", "form", "frame", "frameset",
    "h1", "h2", "h3", "h4", "h5", "h6", "head", "header", "hgroup", "hr", "html", "iframe", "img",
    "input", "keygen", "li", "link", "listing", "main", "marquee", "menu", "meta", "nav",
    "noembed", "noframes", "noscript", "object", "ol", "p", "param", "plaintext", "pre", "script",
    "search", "section", "select", "source", "style", "summary", "table", "tbody", "td",
    "template", "textarea", "tfoot", "th", "thead", "title", "tr", "track", "ul", "wbr", "xmp",
]);
const SCOPE_BOUNDARIES_HTML = new Set([
    "applet", "caption", "html", "table", "td", "th", "marquee", "object", "template",
]);
const MATH_BOUNDARIES = new Set(["mi", "mo", "mn", "ms", "mtext", "annotation-xml"]);
const SVG_BOUNDARIES = new Set(["foreignobject", "desc", "title"]);
const BUTTON_SCOPE = new Set(["button"]);
const LIST_ITEM_SCOPE = new Set(["ol", "ul"]);
/** Current nodes under which a start tag is "in table" rather than in a cell. */
const TABLE_CONTEXT = new Set(["table", "tbody", "thead", "tfoot", "tr"]);
const TABLE_STRUCTURE = new Set([
    "caption", "col", "colgroup", "tbody", "td", "tfoot", "th", "thead", "tr",
]);
const TABLE_CLOSERS_OF_SELECT = new Set([
    "caption", "table", "tbody", "tfoot", "thead", "tr", "td", "th",
]);
/** HTML start tags that end SVG or MathML content (the standard's "breakout" list). */
const FOREIGN_BREAKOUT = new Set([
    "b", "big", "blockquote", "body", "br", "center", "code", "dd", "div", "dl", "dt", "em",
    "embed", "h1", "h2", "h3", "h4", "h5", "h6", "head", "hr", "i", "img", "li", "listing", "menu",
    "meta", "nobr", "ol", "p", "pre", "ruby", "s", "small", "span", "strong", "strike", "sub",
    "sup", "table", "tt", "u", "ul", "var",
]);
const INPUT_TYPES = new Set([
    "hidden", "text", "search", "tel", "url", "email", "password", "date", "month", "week", "time",
    "datetime-local", "number", "range", "color", "checkbox", "radio", "file", "submit", "image",
    "reset", "button",
]);
function findAttribute(attributes, name) {
    return attributes.find((attribute) => attribute.name === name);
}
function hasAttribute(attributes, name) {
    return attributes.some((attribute) => attribute.name === name);
}
function classTokens(attributes) {
    const value = findAttribute(attributes, "class")?.value ?? "";
    return value.split(/[\t\n\f\r ]+/).filter((token) => token !== "");
}
function inputType(attributes) {
    const value = findAttribute(attributes, "type");
    if (value === undefined)
        return "text";
    const lowered = asciiLower(value.value);
    return INPUT_TYPES.has(lowered) ? lowered : "text";
}
function buttonType(attributes) {
    const lowered = asciiLower(findAttribute(attributes, "type")?.value ?? "");
    return lowered === "reset" || lowered === "button" ? lowered : "submit";
}
/** The standard's "rules for parsing non-negative integers", or null when they fail. */
function nonNegativeInteger(value) {
    const match = /^[\t\n\f\r ]*\+?([0-9]+)/.exec(value);
    if (match === null)
        return null;
    const parsed = Number.parseInt(match[1] ?? "", 10);
    return Number.isFinite(parsed) ? parsed : null;
}
/* -- The reader --------------------------------------------------------------------------- */
class PageReader {
    src;
    lower;
    pos = 0;
    plaintext = false;
    stack = [];
    formPointer = null;
    forms = [];
    controls = [];
    tables = [];
    collected = [];
    text = [];
    scriptText = [];
    passwordInputs = 0;
    links = [];
    labels = [];
    linksTruncated = false;
    problems = [];
    /** Set when the page cannot be read further; the loop stops and the page is refused. */
    aborted = false;
    /** Open template elements: counted, so that asking "inside a template?" never walks the stack. */
    templateDepth = 0;
    /** The first element carrying each id: its form when it is a form, otherwise null. */
    ids = new Map();
    collectTextOf;
    constructor(html, options) {
        // The standard normalises every CR LF and lone CR to LF before tokenizing.
        this.src = html.replace(/\r\n?/g, "\n");
        this.lower = asciiLower(this.src);
        this.collectTextOf = options.collectTextOf;
    }
    problem(text) {
        if (this.problems.length < 10 && !this.problems.includes(text))
            this.problems.push(text);
    }
    run() {
        const s = this.src;
        while (this.pos < s.length && !this.aborted) {
            if (this.plaintext) {
                this.characters(s.slice(this.pos));
                this.pos = s.length;
                break;
            }
            const lt = s.indexOf("<", this.pos);
            if (lt < 0) {
                this.characters(s.slice(this.pos));
                this.pos = s.length;
                break;
            }
            if (lt > this.pos)
                this.characters(s.slice(this.pos, lt));
            this.pos = lt;
            this.markup();
        }
        while (this.stack.length > 0)
            this.pop();
        return this.finish();
    }
    /* -- Tokenizer ------------------------------------------------------------------------ */
    markup() {
        const s = this.src;
        const i = this.pos;
        const next = s[i + 1];
        if (isAsciiAlpha(next)) {
            this.tag(false);
            return;
        }
        if (next === "/") {
            const after = s[i + 2];
            if (isAsciiAlpha(after)) {
                this.tag(true);
                return;
            }
            if (after === ">") {
                this.pos = i + 3;
                return;
            }
            if (after === undefined) {
                this.characters("</");
                this.pos = s.length;
                return;
            }
            this.bogusComment(i + 2);
            return;
        }
        if (next === "!") {
            if (s.startsWith("<!--", i)) {
                this.comment(i + 4);
                return;
            }
            if (s.startsWith("<![CDATA[", i)) {
                // Inside SVG or MathML a CDATA section runs to "]]>", not to the first ">": a browser
                // would read markup inside it as text where this reader would read tags.
                this.problem("holds a CDATA section (<![CDATA[), which a browser reads by rules this reader does not follow");
            }
            this.bogusComment(i + 2);
            return;
        }
        if (next === "?") {
            this.bogusComment(i + 1);
            return;
        }
        this.characters("<");
        this.pos = i + 1;
    }
    comment(start) {
        const s = this.src;
        // "<!-->" and "<!--->" are complete (empty) comments.
        if (s[start] === ">") {
            this.pos = start + 1;
            return;
        }
        if (s.startsWith("->", start)) {
            this.pos = start + 2;
            return;
        }
        const plain = s.indexOf("-->", start);
        const bang = s.indexOf("--!>", start);
        if (plain < 0 && bang < 0) {
            this.pos = s.length; // an unclosed comment runs to the end of the page
            return;
        }
        if (plain >= 0 && (bang < 0 || plain <= bang))
            this.pos = plain + 3;
        else
            this.pos = bang + 4;
    }
    bogusComment(start) {
        const end = this.src.indexOf(">", start);
        this.pos = end < 0 ? this.src.length : end + 1;
    }
    tag(isEnd) {
        const s = this.src;
        const n = s.length;
        let j = this.pos + (isEnd ? 2 : 1);
        const nameStart = j;
        while (j < n && !isAsciiWhitespace(s[j]) && s[j] !== "/" && s[j] !== ">")
            j += 1;
        const name = asciiLower(s.slice(nameStart, j));
        const attributes = [];
        let selfClosing = false;
        for (;;) {
            while (j < n && isAsciiWhitespace(s[j]))
                j += 1;
            if (j >= n) {
                // End of page inside a tag: the standard drops the whole tag.
                this.pos = n;
                return;
            }
            const c = s[j];
            if (c === ">") {
                j += 1;
                break;
            }
            if (c === "/") {
                if (s[j + 1] === ">") {
                    selfClosing = true;
                    j += 2;
                    break;
                }
                j += 1;
                continue;
            }
            const nameAt = j;
            j += 1; // the first character belongs to the name, whatever it is (even "=")
            while (j < n && !isAsciiWhitespace(s[j]) && s[j] !== "/" && s[j] !== ">" && s[j] !== "=")
                j += 1;
            const attributeName = asciiLower(s.slice(nameAt, j));
            let k = j;
            while (k < n && isAsciiWhitespace(s[k]))
                k += 1;
            let raw = null;
            if (s[k] === "=") {
                k += 1;
                while (k < n && isAsciiWhitespace(s[k]))
                    k += 1;
                const quote = s[k];
                if (quote === '"' || quote === "'") {
                    const close = s.indexOf(quote, k + 1);
                    if (close < 0) {
                        this.pos = n;
                        return;
                    }
                    raw = s.slice(k + 1, close);
                    j = close + 1;
                }
                else if (quote === ">") {
                    raw = "";
                    j = k;
                }
                else {
                    const valueAt = k;
                    while (k < n && !isAsciiWhitespace(s[k]) && s[k] !== ">")
                        k += 1;
                    if (k >= n) {
                        this.pos = n;
                        return;
                    }
                    raw = s.slice(valueAt, k);
                    j = k;
                }
            }
            else {
                j = k;
            }
            // A repeated attribute is dropped: the first one written is the one a browser keeps.
            if (!attributes.some((attribute) => attribute.name === attributeName)) {
                attributes.push(makeAttribute(attributeName, raw));
            }
        }
        this.pos = j;
        if (isEnd)
            this.endTag(name);
        else
            this.startTag(name, attributes, selfClosing);
    }
    characters(raw) {
        if (raw === "")
            return;
        const decoded = decodeCharacterReferences(raw, false);
        this.onText(decoded.text, decoded.certain);
    }
    /** Read a raw-text element's content up to its own end tag, which is then tokenized as usual. */
    rawText(name) {
        const s = this.src;
        const needle = `</${name}`;
        let from = this.pos;
        let end = s.length;
        for (;;) {
            const at = this.lower.indexOf(needle, from);
            if (at < 0)
                break;
            const after = s[at + needle.length];
            if (after === undefined || isAsciiWhitespace(after) || after === "/" || after === ">") {
                end = at;
                break;
            }
            from = at + 1;
        }
        const raw = s.slice(this.pos, end);
        this.pos = end;
        return raw;
    }
    /**
     * Read a script element's content, which is NOT plain raw text: a script is the one element
     * whose own end tag can be hidden from it, and the rules that hide it are a small state
     * machine of the standard's own. {@link rawText} implements none of that, so script uses this
     * instead, and leaves `pos` on the `</script` that really ends the element.
     *
     * WHY THIS IS NOT THE SAME AS RAW TEXT
     *
     * The reason is a twenty-five-year-old compatibility wart. Before scripts could be served with
     * a type a browser would ignore, pages hid their script from browsers that had none by wrapping
     * it in an HTML comment - `<script><!-- ... --></script>`. Scripts that then wrote further
     * markup (the `document` object's `write` method) were writing strings holding `</script>`, which would otherwise
     * have ended the element in the middle of a string literal. So the standard gave script data a
     * set of "escaped" states in which the element's end is worked out differently, and every
     * browser has implemented them ever since. TMS's day page has used such a script since (at the
     * latest) 21 September 2026, which is when this reader first refused the page.
     *
     * THE STATES, AS SECTION 13.2.5 NAMES THEM
     *
     * Three of the standard's states matter to where the element ends; the rest are the bookkeeping
     * that moves between them, and are folded into the `dashes` counter and the tag tests below.
     *
     *  * "script data state" (13.2.5.4) - the ordinary state. `</script` followed by whitespace,
     *    `/` or `>` ends the element (13.2.5.16 script data end tag open, 13.2.5.17 end tag name,
     *    where "script" is the appropriate end tag name). The ONLY way out is the exact sequence
     *    `<!--`: 13.2.5.15 script data less-than sign takes `<!` to 13.2.5.18 script data escape
     *    start, whose only move is `-` to 13.2.5.19 escape start dash, whose only move is `-` to
     *    13.2.5.21 script data escaped dash dash. Anything else is reconsumed as plain text, so
     *    `<!-` and `<!--x` leave nothing escaped.
     *  * "script data escaped state" (13.2.5.20, with 13.2.5.21 escaped dash and 13.2.5.22 escaped
     *    dash dash) - inside the comment. A `>` returns to script data, but only from escaped dash
     *    dash, i.e. only as `-->`; hence the counter, which any other character resets, `<`
     *    included (13.2.5.23 escaped less-than sign reconsumes into the escaped state, not the
     *    dash one). NOTE, because it surprises people: `</script` DOES still end the element here
     *    (13.2.5.24 escaped end tag open, 13.2.5.25 escaped end tag name). A comment is not a
     *    hiding place; it is only the door to the state where one exists.
     *  * "script data double escaped state" (13.2.5.27, with 13.2.5.28 and 13.2.5.29 for its
     *    dashes) - reached from the escaped state by `<script` plus a delimiter (13.2.5.26 double
     *    escape start, which compares its temporary buffer at the delimiter, so `<scriptx>` is not
     *    a way in). THIS is the hiding place: here `</script` plus a delimiter does not end the
     *    element, it only returns to the escaped state (13.2.5.31 double escape end). `-->` here
     *    goes straight back to script data, not to escaped (13.2.5.29).
     *
     * Walking one character at a time is faithful to the states it folds away, because each of
     * them either consumes only ASCII letters or reconsumes the character that ended it: no `<`
     * can be skipped over unexamined, and no other character can change where the element ends.
     *
     * There is nothing left here to refuse. The element's end is now decided by the same rules the
     * browser decides it by, so every byte of the page after the script is accounted for; the
     * reader's strictness lives on in the problems raised elsewhere, not in a blanket refusal of a
     * construct it can now read.
     */
    scriptData() {
        const s = this.src;
        const start = this.pos;
        let i = start;
        let state = "data";
        /** The run of `-` ending at `i`: `>` closes an escape only on a run of two or more. */
        let dashes = 0;
        while (i < s.length) {
            const c = s[i];
            if (c === "<") {
                if (this.scriptTagAt(i + 1, true)) {
                    // An appropriate end tag: the element's end in both script data and the escaped state,
                    // and only the way out of the double escaped state.
                    if (state !== "double")
                        break;
                    state = "escaped";
                    i += "</script".length;
                    dashes = 0;
                    continue;
                }
                if (state === "data" && s.startsWith("<!--", i)) {
                    state = "escaped";
                    i += "<!--".length;
                    // "<!--" lands in escaped dash dash, so "<!-->" is a complete escape.
                    dashes = 2;
                    continue;
                }
                if (state === "escaped" && this.scriptTagAt(i + 1, false)) {
                    state = "double";
                    i += "<script".length;
                    dashes = 0;
                    continue;
                }
                dashes = 0;
                i += 1;
                continue;
            }
            if (c === "-") {
                if (state !== "data")
                    dashes += 1;
                i += 1;
                continue;
            }
            if (c === ">" && state !== "data" && dashes >= 2) {
                state = "data";
                dashes = 0;
                i += 1;
                continue;
            }
            dashes = 0;
            i += 1;
        }
        const raw = s.slice(start, i);
        this.pos = i;
        return raw;
    }
    /**
     * True when `script` or `/script` starts at `at` and is followed by a character that would end
     * a tag name - the standard's test for the appropriate end tag name and for the temporary
     * buffer of the double escape start and end states.
     */
    scriptTagAt(at, end) {
        const needle = end ? "/script" : "script";
        if (!this.lower.startsWith(needle, at))
            return false;
        const after = this.src[at + needle.length];
        return after === undefined || isAsciiWhitespace(after) || after === "/" || after === ">";
    }
    /* -- Tree builder: start tags --------------------------------------------------------- */
    current() {
        return this.stack[this.stack.length - 1];
    }
    currentIs(name) {
        const current = this.current();
        return current !== undefined && current.ns === "html" && current.name === name;
    }
    startTag(name, attributes, selfClosing) {
        const current = this.current();
        if (current !== undefined && current.ns !== "html" && !this.htmlRulesApply(current, name)) {
            const fontBreakout = name === "font" &&
                (hasAttribute(attributes, "color") || hasAttribute(attributes, "face") || hasAttribute(attributes, "size"));
            if (FOREIGN_BREAKOUT.has(name) || fontBreakout) {
                for (;;) {
                    const top = this.current();
                    if (top === undefined || top.ns === "html" || top.htmlIntegration || top.mathTextIntegration)
                        break;
                    this.pop();
                }
                this.startTag(name, attributes, selfClosing);
                return;
            }
            this.foreignElement(name, current.ns, attributes, selfClosing);
            return;
        }
        const select = this.openSelect();
        if (select !== null) {
            this.startTagInSelect(name, attributes, selfClosing, select);
            return;
        }
        this.htmlStartTag(name, attributes, selfClosing);
    }
    htmlRulesApply(current, name) {
        if (current.htmlIntegration)
            return true;
        if (current.mathTextIntegration)
            return name !== "mglyph" && name !== "malignmark";
        return current.ns === "math" && current.name === "annotation-xml" && name === "svg";
    }
    foreignElement(name, ns, attributes, selfClosing) {
        this.registerId(attributes, null);
        if (selfClosing)
            return; // honoured in SVG and MathML, unlike in HTML
        const encoding = asciiLower(findAttribute(attributes, "encoding")?.value ?? "");
        this.push(name, attributes, false, {
            ns,
            htmlIntegration: (ns === "svg" && SVG_BOUNDARIES.has(name)) ||
                (ns === "math" && name === "annotation-xml" && (encoding === "text/html" || encoding === "application/xhtml+xml")),
            mathTextIntegration: ns === "math" && name !== "annotation-xml" && MATH_BOUNDARIES.has(name),
        });
    }
    /** The select whose content is being parsed, or null (the "in select" insertion mode). */
    openSelect() {
        for (let i = this.stack.length - 1; i >= 0; i -= 1) {
            const element = this.stack[i];
            if (element === undefined || element.ns !== "html")
                return null;
            if (element.name === "select")
                return element;
            if (element.name !== "option" && element.name !== "optgroup")
                return null;
        }
        return null;
    }
    selectIsInTable(select) {
        for (let i = this.stack.indexOf(select) - 1; i >= 0; i -= 1) {
            const element = this.stack[i];
            if (element === undefined)
                break;
            if (element.ns === "html" && element.name === "template")
                return false;
            if (element.ns === "html" && element.name === "table")
                return true;
        }
        return false;
    }
    startTagInSelect(name, attributes, selfClosing, select) {
        switch (name) {
            case "option": {
                if (this.currentIs("option"))
                    this.pop();
                const parent = this.current();
                const option = {
                    valueAttribute: findAttribute(attributes, "value"),
                    selectedAttribute: hasAttribute(attributes, "selected"),
                    disabled: hasAttribute(attributes, "disabled") ||
                        (parent !== undefined && parent.name === "optgroup" && parent.optgroupDisabled),
                    textParts: [],
                    textCertain: true,
                };
                select.select?.rawOptions.push(option);
                this.push("option", attributes, false, { option });
                return;
            }
            case "optgroup":
                if (this.currentIs("option"))
                    this.pop();
                if (this.currentIs("optgroup"))
                    this.pop();
                this.push("optgroup", attributes, false, { optgroupDisabled: hasAttribute(attributes, "disabled") });
                return;
            case "hr":
                // An <hr> ends the option (and the optgroup) it follows, by the standard's select rules,
                // so text after it is not part of that option's label or value.
                if (this.currentIs("option"))
                    this.pop();
                if (this.currentIs("optgroup"))
                    this.pop();
                return;
            case "select":
                // A second select start tag closes the first and is itself dropped.
                this.closeSelect(select, false);
                return;
            case "input":
            case "keygen":
            case "textarea":
                this.closeSelect(select, false);
                this.startTag(name, attributes, selfClosing);
                return;
            case "script":
                this.script(this.scriptData());
                return;
            case "style":
                // Inside a select a browser drops the style tag and reads what follows as markup.
                select.select?.problems.push("holds a style element, which browsers read differently inside a select");
                this.rawText("style");
                return;
            case "template":
                select.select?.problems.push("holds a template element, which this reader does not follow inside a select");
                this.push("template", attributes, false, {});
                return;
            default:
                if (TABLE_CLOSERS_OF_SELECT.has(name) && this.selectIsInTable(select)) {
                    this.closeSelect(select, false);
                    this.startTag(name, attributes, selfClosing);
                }
                // Anything else inside a select is dropped by older parsers and kept as decoration by
                // newer ones. Either way it adds no option and closes nothing, so it is ignored here.
                return;
        }
    }
    htmlStartTag(name, attributes, selfClosing) {
        const current = this.current();
        if (current !== undefined && current.ns === "html" && current.name === "colgroup" && name !== "col" && name !== "template") {
            this.pop();
            this.htmlStartTag(name, attributes, selfClosing);
            return;
        }
        const inTableContext = current !== undefined && current.ns === "html" && TABLE_CONTEXT.has(current.name);
        if ((TABLE_STRUCTURE.has(name) || name === "table") && this.tableStructure(name, attributes, inTableContext)) {
            return;
        }
        let fostered = false;
        if (inTableContext) {
            if (name === "input" && asciiLower(findAttribute(attributes, "type")?.value ?? "") === "hidden") {
                // The one control a table keeps in place: a hidden input.
                this.control("input", attributes, false);
                return;
            }
            if (name === "form") {
                // Inserted where it stands and closed at once; the parser's form pointer stays set.
                this.form(attributes, false, false);
                return;
            }
            if (name !== "style" && name !== "script" && name !== "template")
                fostered = true;
        }
        this.bodyStartTag(name, attributes, selfClosing, fostered);
    }
    /** Table structure tags. Returns true when the tag was handled (or dropped) here. */
    tableStructure(name, attributes, inTableContext) {
        if (name === "table") {
            if (!inTableContext)
                return false; // an ordinary table, perhaps nested in a cell
            // A table start tag directly inside a table closes the open table first.
            if (this.hasInTableScope("table")) {
                this.popUntil("table");
                this.htmlStartTag(name, attributes, false);
            }
            return true;
        }
        if (!inTableContext) {
            // Inside a cell or a caption these first close it; anywhere else the parser drops them.
            if (this.hasInTableScope("td") || this.hasInTableScope("th")) {
                this.popUntilAny(new Set(["td", "th"]));
                this.htmlStartTag(name, attributes, false);
            }
            else if (this.hasInTableScope("caption")) {
                this.popUntil("caption");
                this.htmlStartTag(name, attributes, false);
            }
            return true;
        }
        switch (name) {
            case "caption":
            case "colgroup":
                this.clearToTableContext();
                this.push(name, attributes, false, {});
                return true;
            case "col":
                this.clearToTableContext();
                this.registerId(attributes, null);
                return true;
            case "tbody":
            case "thead":
            case "tfoot":
                this.clearToTableContext();
                this.push(name, attributes, false, {});
                return true;
            case "tr":
                this.clearToTableBodyContext();
                this.pushRow(attributes);
                return true;
            case "td":
            case "th":
                this.clearToTableRowContext();
                this.pushCell(name, attributes);
                return true;
            default:
                return false;
        }
    }
    bodyStartTag(name, attributes, selfClosing, fostered) {
        switch (name) {
            case "html":
            case "head":
            case "body":
            case "frameset":
            case "frame":
                return;
            case "svg":
            case "math":
                this.registerId(attributes, null);
                if (!selfClosing)
                    this.push(name, attributes, fostered, { ns: name === "svg" ? "svg" : "math" });
                return;
            case "form":
                this.form(attributes, true, fostered);
                return;
            case "input":
                this.control("input", attributes, fostered);
                return;
            case "select": {
                const control = this.control("select", attributes, fostered);
                if (hasAttribute(attributes, "multiple")) {
                    control.problems.push("allows several choices (multiple), which this reader does not reproduce");
                }
                const size = findAttribute(attributes, "size");
                if (size !== undefined && nonNegativeInteger(size.value) !== 1) {
                    control.problems.push("is drawn as a list box (size), whose default choice differs from a drop-down");
                }
                this.push("select", attributes, fostered, { select: control });
                return;
            }
            case "textarea": {
                const control = this.control("textarea", attributes, fostered);
                const decoded = decodeCharacterReferences(this.rawText("textarea"), false);
                // A single newline straight after the start tag is not part of the value.
                control.text = decoded.text.startsWith("\n") ? decoded.text.slice(1) : decoded.text;
                if (!decoded.certain) {
                    control.problems.push("holds text with a character reference this reader cannot decode for certain");
                }
                return;
            }
            case "button":
                // A button start tag inside an open button closes the first one.
                if (this.hasInScope("button"))
                    this.popUntil("button");
                this.control("button", attributes, fostered);
                this.push("button", attributes, fostered, {});
                return;
            case "option":
                if (this.currentIs("option"))
                    this.pop();
                this.push("option", attributes, fostered, {});
                return;
            case "optgroup":
                if (this.currentIs("option"))
                    this.pop();
                this.push("optgroup", attributes, fostered, {});
                return;
            case "fieldset":
                this.closePInButtonScope();
                this.push("fieldset", attributes, fostered, { fieldsetDisabled: hasAttribute(attributes, "disabled") });
                return;
            case "legend": {
                const parent = this.current();
                const isFieldsetChild = parent !== undefined && parent.ns === "html" && parent.name === "fieldset";
                const first = isFieldsetChild && !parent.fieldsetSawLegend ? parent : null;
                if (isFieldsetChild)
                    parent.fieldsetSawLegend = true;
                this.push("legend", attributes, fostered, { firstLegendOf: first });
                return;
            }
            case "li":
                this.closeListItem(["li"]);
                this.closePInButtonScope();
                this.push(name, attributes, fostered, {});
                return;
            case "dd":
            case "dt":
                this.closeListItem(["dd", "dt"]);
                this.closePInButtonScope();
                this.push(name, attributes, fostered, {});
                return;
            case "table":
                this.closePInButtonScope();
                this.pushTable(attributes, fostered);
                return;
            case "plaintext":
                this.closePInButtonScope();
                this.registerId(attributes, null);
                this.plaintext = true;
                return;
            default:
                break;
        }
        if (HEADINGS.has(name)) {
            this.closePInButtonScope();
            const top = this.current();
            if (top !== undefined && top.ns === "html" && HEADINGS.has(top.name))
                this.pop();
            this.push(name, attributes, fostered, {});
            return;
        }
        if (CLOSES_P.has(name))
            this.closePInButtonScope();
        if (RAW_TEXT_ELEMENTS.has(name)) {
            this.registerId(attributes, null);
            const raw = name === "script" ? this.scriptData() : this.rawText(name);
            if (name === "script")
                this.script(raw);
            else if (name === "xmp")
                this.onText(raw, true);
            return;
        }
        if (VOID_ELEMENTS.has(name)) {
            this.registerId(attributes, null);
            return;
        }
        this.push(name, attributes, fostered, {});
    }
    form(attributes, keepOpen, fostered) {
        const inTemplate = this.inTemplate();
        // A form start tag inside an open form is dropped by the parser, attributes and all.
        if (this.formPointer !== null && !inTemplate)
            return;
        if (keepOpen)
            this.closePInButtonScope();
        const methodValue = asciiLower(findAttribute(attributes, "method")?.value ?? "");
        const enctypeValue = asciiLower(findAttribute(attributes, "enctype")?.value ?? "");
        const form = {
            index: this.forms.length,
            attributes,
            action: findAttribute(attributes, "action")?.value ?? null,
            method: methodValue === "post" ? "post" : methodValue === "dialog" ? "dialog" : "get",
            enctype: enctypeValue === "multipart/form-data"
                ? "multipart/form-data"
                : enctypeValue === "text/plain"
                    ? "text/plain"
                    : "application/x-www-form-urlencoded",
            acceptCharset: findAttribute(attributes, "accept-charset")?.value ?? null,
            inTemplate,
        };
        this.forms.push(form);
        this.registerId(attributes, form);
        if (!inTemplate)
            this.formPointer = form;
        if (keepOpen)
            this.push("form", attributes, fostered, { formElementOf: form });
    }
    control(tag, attributes, fostered) {
        const inTemplate = this.inTemplate();
        const type = tag === "input" ? inputType(attributes) : tag === "button" ? buttonType(attributes) : tag;
        if (tag === "input" && type === "password" && !inTemplate)
            this.passwordInputs += 1;
        const cell = this.nearestCell();
        const control = {
            index: this.controls.length,
            textIndex: this.text.length,
            tag,
            type,
            name: findAttribute(attributes, "name")?.value ?? "",
            attributes,
            form: null,
            pendingFormId: null,
            disabled: hasAttribute(attributes, "disabled") || this.disabledByFieldset(),
            inDatalist: this.inDatalist(),
            inTemplate,
            problems: [],
            rawOptions: [],
            options: [],
            selectedIndex: -1,
            checked: false,
            text: "",
            row: cell?.row ?? null,
            column: cell?.cell.column ?? null,
            closedWithEndTag: false,
        };
        if (fostered || this.stack.some((element) => element.fostered)) {
            control.problems.push("sits where a browser would move it out of its table (foster parenting), so its place in the post cannot be read for certain");
        }
        if (!inTemplate) {
            const formAttribute = findAttribute(attributes, "form");
            if (formAttribute !== undefined) {
                if (!formAttribute.certain)
                    control.problems.push("names its form with a reference this reader cannot decode for certain");
                control.pendingFormId = formAttribute.value;
            }
            else if (this.formPointer !== null) {
                control.form = this.formPointer;
            }
            else {
                control.form = this.current()?.formAncestor ?? null;
            }
        }
        this.registerId(attributes, null);
        if (!inTemplate) {
            // A control written inside a label is that label's (the standard's labelled control).
            for (const element of this.stack)
                if (element.label !== null)
                    element.label.controls.push(control.index);
        }
        this.controls.push(control);
        return control;
    }
    disabledByFieldset() {
        // Walking outwards: a first legend exempts its own fieldset, never an outer one.
        const exempt = new Set();
        for (let i = this.stack.length - 1; i >= 0; i -= 1) {
            const element = this.stack[i];
            if (element === undefined)
                continue;
            if (element.firstLegendOf !== null)
                exempt.add(element.firstLegendOf);
            if (element.fieldsetDisabled && !exempt.has(element))
                return true;
        }
        return false;
    }
    inDatalist() {
        return this.stack.some((element) => element.ns === "html" && element.name === "datalist");
    }
    inTemplate() {
        return this.templateDepth > 0;
    }
    nearestCell() {
        for (let i = this.stack.length - 1; i >= 0; i -= 1) {
            const element = this.stack[i];
            if (element === undefined)
                continue;
            if (element.cell !== null) {
                for (let j = i - 1; j >= 0; j -= 1) {
                    const row = this.stack[j]?.row;
                    if (row !== null && row !== undefined)
                        return { row, cell: element.cell };
                }
                return null;
            }
            if (element.ns === "html" && element.name === "table")
                return null;
        }
        return null;
    }
    pushTable(attributes, fostered) {
        const table = { index: this.tables.length, rows: [], hasRowspan: false };
        this.tables.push(table);
        this.push("table", attributes, fostered, { table });
    }
    nearestTable() {
        for (let i = this.stack.length - 1; i >= 0; i -= 1) {
            const table = this.stack[i]?.table;
            if (table !== null && table !== undefined)
                return table;
        }
        return null;
    }
    pushRow(attributes) {
        const table = this.nearestTable();
        const row = { index: 0, table: table?.index ?? -1, cells: [], nextColumn: 0 };
        if (table !== null) {
            row.index = table.rows.length;
            table.rows.push(row);
        }
        this.push("tr", attributes, false, { row });
    }
    pushCell(name, attributes) {
        let row = null;
        for (let i = this.stack.length - 1; i >= 0 && row === null; i -= 1)
            row = this.stack[i]?.row ?? null;
        const colspanValue = findAttribute(attributes, "colspan");
        const rowspanValue = findAttribute(attributes, "rowspan");
        const colspanParsed = colspanValue === undefined ? 1 : nonNegativeInteger(colspanValue.value);
        const colspan = colspanParsed === null || colspanParsed === 0 ? 1 : Math.min(colspanParsed, 1000);
        const rowspanParsed = rowspanValue === undefined ? 1 : nonNegativeInteger(rowspanValue.value);
        const rowspan = rowspanParsed === null ? 1 : rowspanParsed;
        const cell = {
            column: row?.nextColumn ?? 0,
            colspan,
            rowspan,
            header: name === "th",
            text: "",
            parts: [],
            size: 0,
        };
        if (row !== null) {
            row.cells.push(cell);
            row.nextColumn += colspan;
        }
        if (rowspan !== 1) {
            const table = this.nearestTable();
            if (table !== null)
                table.hasRowspan = true;
        }
        this.push(name, attributes, false, { cell });
    }
    clearToTableContext() {
        for (;;) {
            const top = this.current();
            if (top === undefined || (top.ns === "html" && (top.name === "table" || top.name === "template")))
                return;
            this.pop();
        }
    }
    clearToTableBodyContext() {
        for (;;) {
            const top = this.current();
            if (top === undefined ||
                (top.ns === "html" && ["tbody", "thead", "tfoot", "table", "template"].includes(top.name))) {
                break;
            }
            this.pop();
        }
        if (this.currentIs("table"))
            this.push("tbody", [], false, {});
    }
    clearToTableRowContext() {
        for (;;) {
            const top = this.current();
            if (top === undefined ||
                (top.ns === "html" && ["tr", "tbody", "thead", "tfoot", "table", "template"].includes(top.name))) {
                break;
            }
            this.pop();
        }
        if (this.currentIs("table"))
            this.push("tbody", [], false, {});
        const top = this.current();
        if (top !== undefined && top.ns === "html" && ["tbody", "thead", "tfoot"].includes(top.name))
            this.pushRow([]);
    }
    closePInButtonScope() {
        if (this.hasInScope("p", BUTTON_SCOPE))
            this.popUntil("p");
    }
    closeListItem(names) {
        for (let i = this.stack.length - 1; i >= 0; i -= 1) {
            const element = this.stack[i];
            if (element === undefined)
                continue;
            if (element.ns === "html" && names.includes(element.name)) {
                this.popUntil(element.name);
                return;
            }
            if (isSpecial(element) && !(element.ns === "html" && ["address", "div", "p"].includes(element.name)))
                return;
        }
    }
    push(name, attributes, fostered, extras) {
        if (this.stack.length >= MAX_HTML_DEPTH) {
            this.problem(`nests elements more than ${String(MAX_HTML_DEPTH)} deep`);
            this.aborted = true;
            return;
        }
        const parent = this.current();
        const formElementOf = extras.formElementOf ?? null;
        const inHtml = (extras.ns ?? "html") === "html" && !this.inTemplate();
        let link = null;
        let label = null;
        if (inHtml && (name === "a" || name === "label")) {
            if (this.links.length + this.labels.length >= MAX_HTML_LINKS) {
                this.linksTruncated = true;
            }
            else if (name === "a") {
                const href = findAttribute(attributes, "href");
                if (href !== undefined) {
                    const cell = this.nearestCell();
                    link = { index: this.links.length, href: href.value, certain: href.certain, text: "", parts: [], size: 0, row: cell?.row ?? null, column: cell?.cell.column ?? null };
                    this.links.push(link);
                }
            }
            else {
                const forId = findAttribute(attributes, "for");
                label = { forId: forId === undefined ? null : forId.value, text: "", parts: [], size: 0, controls: [] };
                this.labels.push(label);
            }
        }
        const element = {
            name,
            ns: extras.ns ?? "html",
            fostered: fostered || (parent?.fostered ?? false),
            formAncestor: formElementOf ?? parent?.formAncestor ?? null,
            formElementOf,
            fieldsetDisabled: extras.fieldsetDisabled ?? false,
            fieldsetSawLegend: false,
            firstLegendOf: extras.firstLegendOf ?? null,
            select: extras.select ?? null,
            option: extras.option ?? null,
            optgroupDisabled: extras.optgroupDisabled ?? false,
            table: extras.table ?? null,
            row: extras.row ?? null,
            cell: extras.cell ?? null,
            collector: null,
            link,
            label,
            htmlIntegration: extras.htmlIntegration ?? false,
            mathTextIntegration: extras.mathTextIntegration ?? false,
        };
        if (this.collectTextOf !== undefined && !this.inTemplate() && this.collected.length < MAX_COLLECTED_ELEMENTS) {
            const classes = classTokens(attributes);
            if (this.collectTextOf(name, classes)) {
                element.collector = { tag: name, classes, text: "", parts: [], size: 0 };
                this.collected.push(element.collector);
            }
        }
        this.registerId(attributes, formElementOf);
        this.stack.push(element);
        if (element.ns === "html" && name === "template")
            this.templateDepth += 1;
    }
    registerId(attributes, form) {
        const id = findAttribute(attributes, "id");
        if (id === undefined || id.value === "" || this.inTemplate())
            return;
        if (!this.ids.has(id.value))
            this.ids.set(id.value, form);
    }
    /* -- Tree builder: end tags and text -------------------------------------------------- */
    pop() {
        const element = this.stack.pop();
        if (element === undefined)
            return;
        if (element.ns === "html" && element.name === "template")
            this.templateDepth -= 1;
        if (element.cell !== null)
            element.cell.text = collapseWhitespace(element.cell.parts.join(""));
        if (element.collector !== null)
            element.collector.text = collapseWhitespace(element.collector.parts.join(""));
        if (element.link !== null)
            element.link.text = collapseWhitespace(element.link.parts.join(""));
        if (element.label !== null)
            element.label.text = collapseWhitespace(element.label.parts.join(""));
        if (element.select !== null && element.name === "select" && !element.select.closedWithEndTag) {
            element.select.problems.push("is not closed with </select>, so which options belong to it cannot be read for certain");
        }
    }
    popUntil(name) {
        while (this.stack.length > 0) {
            const top = this.current();
            this.pop();
            if (top !== undefined && top.ns === "html" && top.name === name)
                return;
        }
    }
    popUntilAny(names) {
        while (this.stack.length > 0) {
            const top = this.current();
            this.pop();
            if (top !== undefined && top.ns === "html" && names.has(top.name))
                return;
        }
    }
    closeSelect(select, withEndTag) {
        if (withEndTag && select.select !== null)
            select.select.closedWithEndTag = true;
        while (this.stack.length > 0) {
            const top = this.current();
            this.pop();
            if (top === select)
                return;
        }
    }
    hasInScope(name, extra) {
        for (let i = this.stack.length - 1; i >= 0; i -= 1) {
            const element = this.stack[i];
            if (element === undefined)
                continue;
            if (element.ns === "html" && element.name === name)
                return true;
            if (isScopeBoundary(element))
                return false;
            if (extra !== undefined && element.ns === "html" && extra.has(element.name))
                return false;
        }
        return false;
    }
    hasInTableScope(name) {
        for (let i = this.stack.length - 1; i >= 0; i -= 1) {
            const element = this.stack[i];
            if (element === undefined)
                continue;
            if (element.ns === "html" && element.name === name)
                return true;
            if (element.ns === "html" && (element.name === "table" || element.name === "template" || element.name === "html")) {
                return false;
            }
        }
        return false;
    }
    endTag(name) {
        const current = this.current();
        if (current !== undefined && current.ns !== "html") {
            for (let i = this.stack.length - 1; i >= 0; i -= 1) {
                const element = this.stack[i];
                if (element === undefined || element.ns === "html")
                    break;
                if (element.name === name) {
                    while (this.stack.length > i)
                        this.pop();
                    return;
                }
            }
        }
        const select = this.openSelect();
        if (select !== null) {
            this.endTagInSelect(name, select);
            return;
        }
        this.htmlEndTag(name);
    }
    endTagInSelect(name, select) {
        switch (name) {
            case "option":
                if (this.currentIs("option"))
                    this.pop();
                return;
            case "optgroup": {
                const below = this.stack[this.stack.length - 2];
                if (this.currentIs("option") && below !== undefined && below.ns === "html" && below.name === "optgroup")
                    this.pop();
                if (this.currentIs("optgroup"))
                    this.pop();
                return;
            }
            case "select":
                this.closeSelect(select, true);
                return;
            default:
                if (TABLE_CLOSERS_OF_SELECT.has(name) && this.selectIsInTable(select) && this.hasInTableScope(name)) {
                    this.closeSelect(select, false);
                    this.htmlEndTag(name);
                }
                return;
        }
    }
    htmlEndTag(name) {
        switch (name) {
            case "p":
                if (this.hasInScope("p", BUTTON_SCOPE))
                    this.popUntil("p");
                return;
            case "li":
                if (this.hasInScope("li", LIST_ITEM_SCOPE))
                    this.popUntil("li");
                return;
            case "dd":
            case "dt":
                if (this.hasInScope(name))
                    this.popUntil(name);
                return;
            case "form":
                this.endForm();
                return;
            case "table":
            case "tbody":
            case "thead":
            case "tfoot":
            case "tr":
            case "td":
            case "th":
            case "caption":
                if (this.hasInTableScope(name))
                    this.popUntil(name);
                return;
            case "colgroup":
                if (this.currentIs("colgroup"))
                    this.pop();
                return;
            case "template":
                if (this.inTemplate())
                    this.popUntil("template");
                return;
            case "body":
            case "html":
            case "br":
                return;
            default:
                break;
        }
        if (VOID_ELEMENTS.has(name))
            return;
        if (HEADINGS.has(name)) {
            if ([...HEADINGS].some((heading) => this.hasInScope(heading)))
                this.popUntilAny(HEADINGS);
            return;
        }
        if (BLOCK_END_TAGS.has(name)) {
            if (this.hasInScope(name))
                this.popUntil(name);
            return;
        }
        // Any other end tag closes the nearest open element of that name, unless a "special"
        // element stands in the way - which is why a stray </span> cannot close a fieldset.
        for (let i = this.stack.length - 1; i >= 0; i -= 1) {
            const element = this.stack[i];
            if (element === undefined)
                continue;
            if (element.ns === "html" && element.name === name) {
                while (this.stack.length > i)
                    this.pop();
                return;
            }
            if (isSpecial(element))
                return;
        }
    }
    endForm() {
        if (this.inTemplate()) {
            if (this.hasInScope("form"))
                this.popUntil("form");
            return;
        }
        const form = this.formPointer;
        this.formPointer = null;
        if (form === null)
            return;
        for (let i = this.stack.length - 1; i >= 0; i -= 1) {
            const element = this.stack[i];
            if (element === undefined)
                continue;
            if (element.formElementOf === form) {
                // Only the form element leaves the stack. Whatever is still open inside it stays open,
                // and stays inside the form - so a control after </form> can still belong to it.
                this.stack.splice(i, 1);
                return;
            }
            if (isScopeBoundary(element))
                return;
        }
    }
    onText(text, certain) {
        if (text === "" || this.inTemplate())
            return;
        for (let i = this.stack.length - 1; i >= 0; i -= 1) {
            const element = this.stack[i];
            if (element === undefined)
                continue;
            if (element.option !== null) {
                element.option.textParts.push(text);
                if (!certain)
                    element.option.textCertain = false;
                break;
            }
            if (element.ns === "html" && element.name === "select")
                break;
        }
        for (const element of this.stack) {
            // Each collector keeps a bounded amount of text, so nested alerts cannot grow quadratically.
            const cell = element.cell;
            if (cell !== null && cell.size < MAX_CELL_CHARS) {
                const piece = text.slice(0, MAX_CELL_CHARS - cell.size);
                cell.parts.push(piece);
                cell.size += piece.length;
            }
            const collector = element.collector;
            if (collector !== null && collector.size < MAX_COLLECTED_CHARS) {
                const piece = text.slice(0, MAX_COLLECTED_CHARS - collector.size);
                collector.parts.push(piece);
                collector.size += piece.length;
            }
            for (const words of [element.link, element.label]) {
                if (words !== null && words.size < MAX_LINK_CHARS) {
                    const piece = text.slice(0, MAX_LINK_CHARS - words.size);
                    words.parts.push(piece);
                    words.size += piece.length;
                }
            }
        }
        this.text.push(text);
    }
    /**
     * A script's text: kept for the notices some pages put there. Where it ends is settled by
     * {@link scriptData} before this is called, escapes and all, so there is nothing to check.
     */
    script(raw) {
        this.scriptText.push(raw);
    }
    /* -- Finishing ------------------------------------------------------------------------ */
    finish() {
        for (const control of this.controls) {
            if (control.pendingFormId !== null)
                control.form = this.ids.get(control.pendingFormId) ?? null;
        }
        const lastCheckedRadio = new Map();
        for (const control of this.controls) {
            if (control.tag === "input" && (control.type === "checkbox" || control.type === "radio")) {
                control.checked = hasAttribute(control.attributes, "checked");
            }
            if (control.tag === "input" && control.type === "radio" && control.checked && control.name !== "") {
                // Checking a radio unchecks the rest of its group, so the last one checked wins.
                const group = JSON.stringify([control.form === null ? -1 : control.form.index, control.name]);
                const previous = lastCheckedRadio.get(group);
                if (previous !== undefined)
                    previous.checked = false;
                lastCheckedRadio.set(group, control);
            }
            if (control.tag === "select") {
                control.options = control.rawOptions.map((option) => {
                    const label = collapseWhitespace(option.textParts.join(""));
                    return {
                        value: option.valueAttribute === undefined ? label : option.valueAttribute.value,
                        label,
                        selectedAttribute: option.selectedAttribute,
                        disabled: option.disabled,
                        certain: option.valueAttribute === undefined ? option.textCertain : option.valueAttribute.certain,
                    };
                });
                control.selectedIndex = selectedOptionIndex(control.options);
            }
        }
        return {
            forms: this.forms,
            controls: this.controls,
            tables: this.tables,
            collected: this.collected,
            text: this.text,
            scriptText: this.scriptText,
            passwordInputs: this.passwordInputs,
            links: this.links.map((link) => ({ index: link.index, href: link.href, certain: link.certain, text: link.text, row: link.row, column: link.column })),
            labels: this.labels.map((label) => ({ forId: label.forId, text: label.text, controls: label.controls })),
            linksTruncated: this.linksTruncated,
            problems: this.problems,
        };
    }
}
function makeAttribute(name, raw) {
    if (raw === null)
        return { name, value: "", certain: true };
    const decoded = decodeCharacterReferences(raw, true);
    // The tokenizer turns a NUL in a value into U+FFFD; that is certain, just unusual.
    return { name, value: decoded.text.split(NUL).join(REPLACEMENT_CHARACTER), certain: decoded.certain };
}
function isScopeBoundary(element) {
    if (element.ns === "html")
        return SCOPE_BOUNDARIES_HTML.has(element.name);
    if (element.ns === "math")
        return MATH_BOUNDARIES.has(element.name);
    return SVG_BOUNDARIES.has(element.name);
}
function isSpecial(element) {
    if (element.ns === "html")
        return SPECIAL_HTML.has(element.name);
    return isScopeBoundary(element);
}
/**
 * The standard's selectedness rules for a drop-down, applied to the finished option list:
 * of the options marked `selected`, the LAST one is selected; with none marked, the first
 * option that is not disabled; with every option disabled, none.
 */
function selectedOptionIndex(options) {
    let chosen = -1;
    for (let i = 0; i < options.length; i += 1) {
        if (options[i]?.selectedAttribute === true)
            chosen = i;
    }
    if (chosen >= 0)
        return chosen;
    return options.findIndex((option) => !option.disabled);
}
/**
 * Read a page. Never throws on markup: every oddity is either reproduced as a browser would, or
 * recorded as a problem on the control it affects.
 */
export function readHtmlPage(html, options = {}) {
    const source = String(html);
    if (source.length > MAX_HTML_PAGE_CHARS) {
        return {
            forms: [],
            controls: [],
            tables: [],
            collected: [],
            text: [],
            scriptText: [],
            passwordInputs: 0,
            links: [],
            labels: [],
            linksTruncated: false,
            problems: [`is ${String(source.length)} characters long, more than the ${String(MAX_HTML_PAGE_CHARS)} this reader reads`],
        };
    }
    return new PageReader(source, options).run();
}
/** A name quoted in a problem sentence: control characters out, and never more than 60 long. */
function quotedName(name) {
    const flat = collapseWhitespace(name.replace(/[\x00-\x1f\x7f-\x9f]/g, " "));
    return flat.length > 60 ? `${flat.slice(0, 57)}...` : flat;
}
/* -- The entry list: what a browser submits for a form ------------------------------------- */
export function attributeOf(control, name) {
    return findAttribute(control.attributes, name);
}
function describeControl(control) {
    const label = control.name === "" ? "(unnamed)" : `"${quotedName(control.name)}"`;
    if (control.tag === "input")
        return `the ${control.type} input ${label}`;
    return `the ${control.tag} ${label}`;
}
function isSubmitButton(control) {
    if (control.tag === "button")
        return control.type === "submit";
    return control.tag === "input" && (control.type === "submit" || control.type === "image");
}
/** The standard turns every line break in a submitted name or value into CR LF. */
function normaliseNewlines(value) {
    return value.replace(/\r\n|\r|\n/g, "\r\n");
}
/**
 * The exact list of name/value pairs a browser submits for `form` when the TD presses its one
 * Save button, in page order - or the reason it cannot be reproduced with certainty.
 *
 * It follows the standard's "constructing the entry list": controls owned by the form, in page
 * order; skipped when disabled (by attribute or fieldset), inside a datalist, unnamed, an
 * unchecked checkbox or radio, a button other than the one pressed, or a select whose selected
 * option is disabled; a checkbox without a value sends "on"; a hidden `_charset_` sends the
 * charset; every line break becomes CR LF.
 */
export function formEntryList(page, form) {
    const owned = page.controls.filter((control) => control.form === form && !control.inTemplate);
    const refuse = (problem) => ({ ok: false, problem });
    // A page this reader cannot read in full gives no entries at all: what it did read may be wrong.
    const pageProblem = page.problems[0];
    if (pageProblem !== undefined)
        return refuse(`the page ${pageProblem}`);
    for (const control of owned) {
        const problem = control.problems[0];
        if (problem !== undefined)
            return refuse(`${describeControl(control)} ${problem}`);
    }
    // The submitter: the button the TD presses. With several, it must not matter which.
    const buttons = owned.filter((control) => !control.disabled && !control.inDatalist && isSubmitButton(control));
    let submitter = null;
    if (buttons.length === 1) {
        submitter = buttons[0] ?? null;
    }
    else if (buttons.some((button) => button.name !== "" || button.type === "image")) {
        return refuse(`the form has ${String(buttons.length)} submit buttons and at least one sends a field of its own, so which one is pressed would change what is sent`);
    }
    if (submitter !== null) {
        if (submitter.type === "image")
            return refuse("the form's submit button is an image, which sends the position of the click");
        for (const override of ["formaction", "formmethod", "formenctype"]) {
            if (attributeOf(submitter, override) !== undefined) {
                return refuse(`the form's submit button carries ${override}, which changes where or how the form is sent`);
            }
        }
        if (submitter.name !== "" && submitter.tag === "input" && attributeOf(submitter, "value") === undefined) {
            return refuse("the form's submit button sends its caption, which browsers word differently");
        }
    }
    const entries = [];
    for (const control of owned) {
        const entry = entryFor(control, submitter);
        if (entry === null)
            continue;
        if (typeof entry === "string")
            return refuse(entry);
        entries.push({ name: normaliseNewlines(entry.name), value: normaliseNewlines(entry.value) });
    }
    return { ok: true, entries };
}
/** One control's contribution: an entry, nothing (null), or why it cannot be reproduced. */
function entryFor(control, submitter) {
    if (control.disabled || control.inDatalist || control.name === "")
        return null;
    const described = describeControl(control);
    const valueAttribute = attributeOf(control, "value");
    let value;
    let certain = valueAttribute?.certain ?? true;
    switch (control.tag) {
        case "button":
            if (control !== submitter)
                return null;
            value = valueAttribute?.value ?? "";
            break;
        case "select": {
            const option = control.options[control.selectedIndex];
            if (option === undefined || option.disabled)
                return null;
            value = option.value;
            certain = option.certain;
            break;
        }
        case "textarea":
            if (asciiLower(attributeOf(control, "wrap")?.value ?? "") === "hard") {
                return `${described} wraps its text hard, which inserts line breaks this reader does not reproduce`;
            }
            if (attributeOf(control, "dirname") !== undefined) {
                return `${described} carries dirname, which adds a field this reader does not reproduce`;
            }
            value = control.text;
            certain = true; // an uncertain textarea is already a problem on the control
            break;
        default:
            switch (control.type) {
                case "submit":
                    if (control !== submitter)
                        return null;
                    value = valueAttribute?.value ?? "";
                    break;
                case "image":
                case "reset":
                case "button":
                    return null;
                case "checkbox":
                case "radio":
                    if (!control.checked)
                        return null;
                    value = valueAttribute?.value ?? "on";
                    break;
                case "hidden":
                    if (asciiLower(control.name) === "_charset_")
                        return { name: control.name, value: "UTF-8" };
                    value = valueAttribute?.value ?? "";
                    break;
                case "text":
                case "search":
                case "tel":
                case "password":
                    if (attributeOf(control, "dirname") !== undefined) {
                        return `${described} carries dirname, which adds a field this reader does not reproduce`;
                    }
                    // The standard's value sanitisation for these types strips line breaks.
                    value = (valueAttribute?.value ?? "").replace(/[\r\n]/g, "");
                    break;
                default:
                    return `${described} is a type whose value a browser rewrites or uploads, which this reader does not reproduce`;
            }
    }
    if (attributeOf(control, "name")?.certain === false) {
        return `${described} has a name holding a character reference this reader cannot decode for certain`;
    }
    if (!certain)
        return `${described} has a value holding a character reference this reader cannot decode for certain`;
    return { name: control.name, value };
}
/* -- Serialising ---------------------------------------------------------------------------- */
/** Replace unpaired surrogates with U+FFFD, as encoding a string to UTF-8 does. */
function wellFormed(value) {
    if (!/[\ud800-\udfff]/.test(value))
        return value;
    let out = "";
    for (let i = 0; i < value.length; i += 1) {
        const code = value.charCodeAt(i);
        if (code >= 0xd800 && code <= 0xdbff) {
            const next = value.charCodeAt(i + 1);
            if (next >= 0xdc00 && next <= 0xdfff) {
                out += value.slice(i, i + 2);
                i += 1;
                continue;
            }
            out += REPLACEMENT_CHARACTER;
            continue;
        }
        out += code >= 0xdc00 && code <= 0xdfff ? REPLACEMENT_CHARACTER : value[i];
    }
    return out;
}
function encodeFormComponent(value) {
    return encodeURIComponent(wellFormed(value))
        .replace(/[!'()~]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`)
        .replace(/%20/g, "+");
}
/**
 * `application/x-www-form-urlencoded`, byte for byte what `URLSearchParams.toString()` gives for
 * the same pairs: UTF-8, spaces as "+", and only `*-._` and letters and digits left bare. The
 * same entries always give the same string, so a body can be compared and pinned exactly.
 */
export function encodeFormUrlEncoded(entries) {
    return entries.map((entry) => `${encodeFormComponent(entry.name)}=${encodeFormComponent(entry.value)}`).join("&");
}
//# sourceMappingURL=tms-html-form.js.map